// src/shared/composables/useFileDownloader.ts
//
// Fetch ciphertext → decrypt with WebCrypto → recover original filename + MIME
// → trigger browser download via Blob URL.

import { ref } from 'vue'
import { useApi } from './useApi'
import { decryptFile, decryptFileChunked, type PlainFileMetadata } from './useE2EEncryption'
import {
  downloadEncryptedFile,
  fetchFileMetadata,
  fetchManifest,
  downloadChunk,
  type FileMetadataResponse,
} from '@/api/files'

export interface DownloadResult {
  filename: string
  mimeType: string
  sizeBytes: number
}

/**
 * Guarda bytes descifrados en disco disparando un anchor.click() en el navegador.
 * Reutilizado por las rutas mono y chunked.
 */
function saveBlob(bytes: Uint8Array, metadata: PlainFileMetadata): void {
  // slice() garantiza un ArrayBuffer propio (evita SharedArrayBuffer en tipos estrictos)
  const blob = new Blob([bytes.slice()], { type: metadata.mimeType })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = metadata.filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

export function useFileDownloader() {
  const api = useApi()
  const downloading = ref(false)
  const error = ref<string | null>(null)

  async function inspect(identifier: string): Promise<FileMetadataResponse> {
    return fetchFileMetadata(api, identifier)
  }

  async function download(
    identifier: string,
    passphrase: string,
  ): Promise<DownloadResult> {
    error.value = null
    downloading.value = true
    try {
      // Consultar metadata para saber si es chunked antes de descargar
      const meta = await fetchFileMetadata(api, identifier)

      // --- Ruta chunked ---
      if (meta.chunked) {
        const manifest = await fetchManifest(api, identifier)
        const total = Number(manifest.total_chunks)
        if (!Number.isInteger(total) || total <= 0) {
          throw new Error('manifest inválido: total_chunks')
        }
        const chunksB64: string[] = []
        for (let i = 0; i < total; i++) {
          const c = await downloadChunk(api, identifier, i, manifest.download_token)
          chunksB64.push(c.content_base64)
        }
        const decrypted = await decryptFileChunked(
          {
            saltB64: manifest.salt_b64,
            baseNonceB64: manifest.base_nonce_b64,
            totalChunks: total,
          },
          chunksB64,
          passphrase,
        )
        saveBlob(decrypted.bytes, decrypted.metadata)
        return {
          filename: decrypted.metadata.filename,
          mimeType: decrypted.metadata.mimeType,
          sizeBytes: decrypted.bytes.byteLength,
        }
      }

      // --- Ruta mono ---
      const ciphertext = await downloadEncryptedFile(api, identifier)
      const decrypted = await decryptFile(
        {
          ivB64: ciphertext.iv_b64,
          saltB64: ciphertext.salt_b64,
          ciphertextB64: ciphertext.ciphertext_b64,
        },
        passphrase,
      )

      saveBlob(new Uint8Array(decrypted.bytes), decrypted.metadata)

      return {
        filename: decrypted.metadata.filename,
        mimeType: decrypted.metadata.mimeType,
        sizeBytes: decrypted.bytes.byteLength,
      }
    } catch (ex) {
      const msg =
        ex instanceof Error
          ? /OperationError|tag/.test(ex.message)
            ? 'Invalid passphrase or tampered file'
            : ex.message
          : String(ex)
      error.value = msg
      throw new Error(msg)
    } finally {
      downloading.value = false
    }
  }

  return { inspect, download, downloading, error }
}
